/**
 * AI JSON Parser - TypeScript Version
 *
 * A robust utility for parsing JSON responses from AI models that handles
 * common issues in LLM-generated JSON.
 *
 * @module AIJSONParser
 * @version 1.0.0
 */

/**
 * Response object structure from an AI service
 */
export interface AIResponseObject {
  content: string;
  model?: string;
  provider?: string;
  timestamp?: string;
  [key: string]: any;
}

/**
 * Options for parsing AI responses
 */
export interface ParseOptions {
  /** Include debug information in fallback results */
  debug?: boolean;
  /** Return partial results in case of parsing failures */
  allowPartial?: boolean;
}

/**
 * Error result from parsing
 */
export interface ParseError {
  error: true;
  message: string;
  originalError?: string;
  originalContent?: string;
}

/**
 * Result with fallback parsing info
 */
export interface ParseResultWithFallback {
  _parsingInfo: {
    method: 'fallback';
    error: string;
    cleaned: string;
  };
  [key: string]: any;
}

/**
 * Successful parse result (actual content depends on the JSON)
 */
export type ParseSuccess = Record<string, any>;

/**
 * Combined type for all possible parse results
 */
export type ParseResult = ParseSuccess | ParseError | ParseResultWithFallback;

/**
 * Parses a response object from an LLM to extract and clean the embedded JSON content
 *
 * @param responseObject - The response object from the AI service
 * @param options - Parsing options
 * @returns The parsed JSON content, with error info if parsing failed
 */
export function parseAIResponse(responseObject: AIResponseObject, options: ParseOptions = {}): ParseResult {
  const { debug = false, allowPartial = true } = options;

  try {
    // Check for valid input
    if (!responseObject || typeof responseObject !== 'object') {
      throw new Error('Invalid response object provided');
    }

    // Extract the content string from the response object
    const contentString = responseObject.content || '';

    // Extract JSON string from various formats
    const jsonString = extractJSONString(contentString);

    if (!jsonString) {
      throw new Error('Could not extract JSON string from content');
    }

    // Clean the JSON string to fix common issues
    const cleanedJsonString = cleanJSONString(jsonString);

    // First attempt: Parse the cleaned JSON directly
    try {
      return JSON.parse(cleanedJsonString);
    } catch (parsingError) {
      if (debug) console.error('Initial JSON parsing failed:', (parsingError as Error).message);

      // Second attempt: More aggressive cleaning for special cases
      const aggressivelyCleanedJsonString = aggressiveJSONClean(cleanedJsonString);

      try {
        return JSON.parse(aggressivelyCleanedJsonString);
      } catch (secondError) {
        if (debug) console.error('Secondary JSON parsing failed:', (secondError as Error).message);

        // If partial results are not allowed, throw the error
        if (!allowPartial) {
          throw secondError;
        }

        // Final attempt: Extract key-value pairs for partial results
        const result = extractKeyValuePairs(aggressivelyCleanedJsonString);

        // Include debug info if requested
        if (debug) {
          (result as ParseResultWithFallback)._parsingInfo = {
            method: 'fallback',
            error: (secondError as Error).message,
            cleaned: aggressivelyCleanedJsonString,
          };
        }

        return result;
      }
    }
  } catch (error) {
    if (debug) console.error('Error in parseAIResponse:', error);

    return {
      error: true,
      message: 'Failed to parse response content',
      originalError: (error as Error).message,
      originalContent: responseObject?.content,
    };
  }
}

/**
 * Extracts a JSON string from content that might be wrapped in code blocks
 *
 * @param contentString - The content string that might contain JSON
 * @returns The extracted JSON string, or null if not found
 */
export function extractJSONString(contentString: string): string | null {
  // Case 1: JSON within ```json code blocks
  if (contentString.includes('```json')) {
    const jsonMatch = contentString.match(/```json\s*\n([\s\S]*?)\n\s*```/);
    if (jsonMatch && jsonMatch[1]) {
      return jsonMatch[1];
    }
  }

  // Case 2: JSON within generic ``` code blocks
  if (contentString.includes('```')) {
    const jsonMatch = contentString.match(/```\s*\n([\s\S]*?)\n\s*```/);
    if (jsonMatch && jsonMatch[1]) {
      return jsonMatch[1];
    }
  }

  // Case 3: Direct JSON string (assume the entire content is JSON)
  return contentString;
}

/**
 * Cleans a JSON string to fix common formatting issues
 *
 * @param jsonString - The JSON string to clean
 * @returns The cleaned JSON string
 */
export function cleanJSONString(jsonString: string): string {
  if (!jsonString) return '';

  let result = jsonString
    // 1. Remove all types of JavaScript comments
    .replace(/\/\/.*$/gm, '') // Remove single line comments
    .replace(/\/\*[\s\S]*?\*\//g, ''); // Remove multi-line comments

  // 2. Remove trailing commas in objects and arrays
  result = result.replace(/,\s*([\]}])/g, '$1');

  // 3. Add missing quotes around unquoted property keys
  result = result.replace(/([{,]\s*)([a-zA-Z0-9_]+)(\s*:)/g, '$1"$2"$3');

  // 4. Handle Unicode Escape Sequences
  result = result.replace(/\\u([a-fA-F0-9]{4})/g, (match: string, p1: string) => {
    return String.fromCharCode(parseInt(p1, 16));
  });

  return result;
}

/**
 * Performs aggressive cleaning on a JSON string for difficult cases
 *
 * @param jsonString - The JSON string to clean
 * @returns The aggressively cleaned JSON string
 */
export function aggressiveJSONClean(jsonString: string): string {
  if (!jsonString) return '';

  let cleanedString = jsonString;

  // 1. Try to fix missing commas between key-value pairs
  cleanedString = cleanedString.replace(/"([^"]*)"\s*\n\s*"/g, '$1,\n"');

  // 2. Handle unescaped quotes in JSON string values (more complex)
  cleanedString = cleanedString.replace(/"([^"\\]*(?:\\.[^"\\]*)*)"/g, match => {
    // Replace unescaped quotes within the string value
    return match.replace(/(?<!\\)"/g, '\\"');
  });

  // 3. Remove any remaining comments that might have been missed
  cleanedString = cleanedString.replace(/\/\/.*$/gm, '').replace(/\/\*[\s\S]*?\*\//g, '');

  // 4. Replace tabs with spaces for consistent formatting
  cleanedString = cleanedString.replace(/\t/g, '  ');

  return cleanedString;
}

/**
 * Extracts key-value pairs from a JSON string as a fallback method
 *
 * @param jsonString - The JSON string to extract from
 * @returns An object with extracted key-value pairs
 */
export function extractKeyValuePairs(jsonString: string): Record<string, any> {
  const result: Record<string, any> = {};

  // Extract keys and values using a more forgiving approach
  const validKeyValuePairs = jsonString
    .split('\n')
    .map(line => line.trim())
    .filter(line => /^"[^"]+"\s*:/.test(line));

  validKeyValuePairs.forEach(pair => {
    const colonIndex = pair.indexOf(':');
    if (colonIndex !== -1) {
      // Extract key (removing surrounding quotes)
      const key = pair.substring(1, colonIndex - 1).trim();

      // Extract value and handle different types
      let valueStr = pair.substring(colonIndex + 1).trim();
      // Remove trailing comma if present
      if (valueStr.endsWith(',')) {
        valueStr = valueStr.slice(0, -1).trim();
      }

      let value: any;

      // Try to parse the value according to its type
      if (valueStr === 'true') {
        value = true;
      } else if (valueStr === 'false') {
        value = false;
      } else if (valueStr === 'null') {
        value = null;
      } else if (!isNaN(Number(valueStr))) {
        value = Number(valueStr);
      } else if (valueStr.startsWith('"') && valueStr.endsWith('"')) {
        // String value
        value = valueStr.substring(1, valueStr.length - 1);
      } else if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
        // Array (basic handling)
        try {
          value = JSON.parse(valueStr);
        } catch (e) {
          value = valueStr; // Fallback to string if parsing fails
        }
      } else if (valueStr.startsWith('{') && valueStr.endsWith('}')) {
        // Object (basic handling)
        try {
          value = JSON.parse(valueStr);
        } catch (e) {
          value = valueStr; // Fallback to string if parsing fails
        }
      } else {
        value = valueStr; // Keep as string
      }

      result[key] = value;
    }
  });

  return result;
}

/**
 * Check if the JSON content is wrapped in a code block
 *
 * @param content - The content to check
 * @returns True if the content is wrapped in a code block
 */
export function isCodeBlock(content: string): boolean {
  return content.trim().startsWith('```') && content.trim().endsWith('```');
}

/**
 * Check if a parsing result contains errors
 *
 * @param result - The parsing result to check
 * @returns True if the result contains errors
 */
export function hasParsingErrors(result: ParseResult): boolean {
  return (result as ParseError).error === true || !!(result as ParseResultWithFallback)._parsingInfo;
}

/**
 * Extract any available data from a possibly errored result
 *
 * @param result - The parsing result
 * @returns An object with any available data
 */
export function extractDataFromResult(result: ParseResult): Record<string, any> {
  if (!result || typeof result !== 'object') {
    return {};
  }

  // If there's no error, return the whole object (minus any parsing info)
  if (!hasParsingErrors(result)) {
    const { _parsingInfo, ...data } = result as ParseResultWithFallback;
    return data;
  }

  // If there is an error but we have some data, extract what we can
  const { error, message, originalError, originalContent, _parsingInfo, ...data } = result as ParseError &
    ParseResultWithFallback;

  return Object.keys(data).length > 0 ? data : {};
}

export default {
  parseAIResponse,
  extractJSONString,
  cleanJSONString,
  aggressiveJSONClean,
  extractKeyValuePairs,
  isCodeBlock,
  hasParsingErrors,
  extractDataFromResult,
};
